import {Component, EventEmitter, Input, Output} from '@angular/core';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {GATEWAY_TYPE} from '@constants/gateway/gateway-type';
import {TranslateService} from '@ngx-translate/core';
import {ConfirmationDialogService} from '@services/confirmation-dialog.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {GatewayService} from '@services/administration/gateway/gateway.service';
import {NotificationService} from '@services/notification/notification.service';
import {Router} from '@angular/router';
import {btou} from '@prototypes/global.functions';

@Component({
    selector: 'scitis-admin-gateway-action',
    templateUrl: './gateway-action.component.html',
    styleUrls: ['./gateway-action.component.scss'],
    standalone: false
})
export class GatewayActionComponent {

  @Input()
  isAdminRole: boolean = false;

  @Input()
  isDistributorRole: boolean = false;

  @Input()
  isSuperAdminRole: boolean = false;

  @Input()
  isTechAdminRole: boolean = false;

  @Input()
  gateway: Gateway = new Gateway();

  @Input()
  searchTerm: string = '';

  gatewayType: typeof GATEWAY_TYPE = GATEWAY_TYPE;

  @Output()
  gatewayDeleteEvent = new EventEmitter<Gateway>();

  @Output()
  gatewayUpdatedEvent = new EventEmitter<Gateway>();

  private COMPONENT_NAME: string = 'GatewayActionComponent';
  private readonly accessToGatewayNotAllowedMessage = '';

  constructor(private translate: TranslateService,
              private confirmationDialogService: ConfirmationDialogService,
              private loadingIndicator: LoadingIndicatorService,
              private gatewayService: GatewayService,
              private notification: NotificationService,
              private router: Router) {
    this.accessToGatewayNotAllowedMessage = this.translate.instant('You don\'t have access to that gateway because they are not in your machinery groups. Please contact your portal provider for details.');
  }

  onDeleteGateway() {
    console.log(`${this.COMPONENT_NAME}. removing gateway: ${this.gateway.name}`);
    const removeMessageTitle = this.translate.instant('Delete gateway');
    const removeMessageBody = this.translate.instant('Do you really want to permanently delete the gateway %s ?').replace('%s', this.gateway.name.toBold() + '(' + this.gateway.id + ')');
    const removeMessageOK = this.translate.instant('Delete gateway');

    this.confirmationDialogService.handleConfirmationDialog(removeMessageTitle, removeMessageBody, '',
        removeMessageOK).subscribe(userConfirmed => {
      if (userConfirmed) {
        this.loadingIndicator.show(this.translate.instant('Deleting'));
        this.gatewayService.deleteGateway(this.gateway.id).subscribe(() => {
          this.gatewayDeleteEvent.emit(this.gateway);
          this.loadingIndicator.hide();
          this.notification.success(this.translate.instant('Successfully deleted gateway'), this.COMPONENT_NAME, false);
        }, (error) => {
          this.loadingIndicator.hide();
          this.notification.error(this.translate.instant('Gateway could not be deleted'), error, this.COMPONENT_NAME, true);
        });
      }
    }, () => {
      this.loadingIndicator.hide();
    });
  }

  onEditGateway() {
    this.loadingIndicator.show();
    this.gatewayService.gatewayIsAvailableForUser(this.gateway.id).subscribe((isAvailable: boolean) => {
      if (isAvailable) {
        const parameters = {
          id: this.gateway.id,
          isAdminRole: this.isAdminRole,
          isSuperAdminRole: this.isSuperAdminRole,
          isTechAdminRole: this.isTechAdminRole
        };
        const encodedParams = btou(JSON.stringify(parameters));
        this.router.navigate(['gateways', encodedParams], {state: {search: this.searchTerm}});
      } else {
        this.notification.error(this.accessToGatewayNotAllowedMessage, null, this.COMPONENT_NAME, true);
      }
      this.loadingIndicator.hide();
    }, () => {
      this.loadingIndicator.hide();
    });
  }

  onResetSharedKey() {
    console.log(`${this.COMPONENT_NAME}. reset gateway: ${this.gateway.name}`);
    const resetMessageTitle = this.translate.instant('Reset gateway secret key');
    const resetMessageBody = this.translate.instant('Do you really want to reset the secret key for gateway?') + ' ' + this.translate.instant('Name') + ': ' + this.gateway.name + '(' + this.gateway.id + ')';
    const resetMessageOK = this.translate.instant('Reset');

    this.confirmationDialogService.handleConfirmationDialog(resetMessageTitle, resetMessageBody, '',
        resetMessageOK).subscribe(userConfirmed => {
      if (userConfirmed) {
        this.loadingIndicator.show(this.translate.instant('Resetting'));
        this.gatewayService.resetSecretKey(this.gateway.id).subscribe(() => {
          this.gatewayUpdatedEvent.emit(this.gateway);
          this.loadingIndicator.hide();
          this.notification.success(this.translate.instant('Secret key reset'), this.COMPONENT_NAME, false);
        }, (error) => {
          this.loadingIndicator.hide();
          this.notification.error(this.translate.instant('Gateway secret key could not be reset'), error, this.COMPONENT_NAME, true);
        });
      }
    }, (error) => {
      console.log(error);
      this.loadingIndicator.hide();
    });
  }

}
